<script setup lang="ts">
/**
 * Small status pill. Variants map to neutral/positive/warning/negative
 * so feature code stays semantic rather than hard-coding colours.
 */
import { computed, useAttrs } from 'vue'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/shared/lib/cn'

const badge = cva(
  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium leading-none',
  {
    variants: {
      variant: {
        neutral: 'bg-muted text-muted-foreground',
        positive: 'bg-primary/15 text-primary',
        warning: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400',
        negative: 'bg-destructive/15 text-destructive',
      },
    },
    defaultVariants: { variant: 'neutral' },
  },
)

interface Props {
  variant?: VariantProps<typeof badge>['variant']
}

const props = withDefaults(defineProps<Props>(), { variant: 'neutral' })
const attrs = useAttrs()
const classes = computed(() => cn(badge({ variant: props.variant }), (attrs.class as string) ?? ''))
</script>

<template>
  <span :class="classes">
    <slot />
  </span>
</template>
